import { CampaignInfo } from '@/providers/campaign-provider'
import type { CartMiniResponse } from '@/types/cart'
import { ProductFormatted } from '@/types/product'
import { ProductDetailFormatted } from '@/types/product-detail'
import { CAMPAIGN_NAMES } from './campaign/campaign-names'
import { calculateBuy1Get1ValueMatchPrice } from './campaign/campaign-price-buy-1-get-1-value-match'
import { calculateBuyMoreSaveMorePrice } from './campaign/campaign-price-buy-more-save-more'

export interface CampaignPriceParams {
  regularPrice: number
  comparePrice: number
  maxPromotionAmount: number
  campaignPercent: number
  isCampaignActive: boolean
  isExcluded: boolean
  maxEligiblePriceForGift?: number // from cart-mini for buy_1_get_1_value_match campaign
  isBuy1Get1ValueMatchActive?: boolean // campaign active status for buy_1_get_1_value_match
}

export interface CampaignPriceResult {
  finalPrice: number
  originalPrice: number
  discountPercent: number
  shouldShowDiscount: boolean
}

/**
 * Utility function tính campaign price không phụ thuộc vào hooks.
 * Sử dụng cùng logic với useCampaignPrice hook.
 * Được dùng để snapshot giá tại thời điểm add to cart, tránh bị ảnh hưởng bởi invalidate queries.
 *
 * Priority: buy_1_get_1_value_match > buy_more_save_more
 */
export function calculateCampaignPrice({
  regularPrice,
  comparePrice,
  maxPromotionAmount,
  campaignPercent,
  isCampaignActive,
  isExcluded,
  maxEligiblePriceForGift,
  isBuy1Get1ValueMatchActive,
}: CampaignPriceParams): CampaignPriceResult {
  // Giá gốc để so sánh là comparePrice nếu có, nếu không thì lấy regularPrice
  const originalPrice =
    typeof comparePrice === 'number' &&
    comparePrice > 0 &&
    comparePrice > regularPrice
      ? comparePrice
      : regularPrice

  // Nếu campaign không hoạt động, trả về giá gốc và không có giảm giá
  if (isExcluded) {
    const salePercent =
      originalPrice > 0
        ? Math.round(((originalPrice - regularPrice) / originalPrice) * 100)
        : 0
    return {
      finalPrice: regularPrice,
      originalPrice: comparePrice,
      discountPercent: salePercent,
      shouldShowDiscount: salePercent > 0 && originalPrice > regularPrice,
    }
  }

  // Priority 1: Check buy_1_get_1_value_match campaign first (higher priority)
  const buy1Get1ValueMatchResult = calculateBuy1Get1ValueMatchPrice({
    regularPrice,
    originalPrice,
    maxEligiblePriceForGift,
    isBuy1Get1ValueMatchActive,
  })

  if (buy1Get1ValueMatchResult) {
    return buy1Get1ValueMatchResult
  }

  // Priority 2: Fall back to buy_more_save_more campaign logic
  return calculateBuyMoreSaveMorePrice({
    originalPrice,
    maxPromotionAmount,
    campaignPercent,
    isCampaignActive,
  })
}

export const isProductExcluded = (
  product: ProductFormatted | ProductDetailFormatted | undefined,
  campaignData: CampaignInfo
) => {
  const excludeCollections =
    (campaignData?.data?.exclude_collection_ids as string[]) ?? []
  if (!product || !product.collections || excludeCollections.length === 0) {
    return false
  }

  if (typeof product.collections[0] === 'string') {
    return product.collections.some((c) =>
      excludeCollections.includes(c as string)
    )
  } else {
    const productCollections = product.collections as { value: string }[]
    return productCollections.some((c) => excludeCollections.includes(c.value))
  }
}

/**
 * Detect campaign nào đang được áp dụng cho pricing dựa trên priority logic
 * Giống với logic trong calculateCampaignPrice để đảm bảo consistency
 *
 * Priority: buy_1_get_1_value_match > buy_more_save_more
 *
 * @param campaigns - Tất cả campaigns từ CampaignProvider
 * @param regularPrice - Giá thường của sản phẩm
 * @param maxEligiblePriceForGift - Giá tối đa được tặng từ cart-mini (cho buy_1_get_1_value_match)
 * @returns Campaign đang được áp dụng hoặc null
 */
export function getActiveCampaignForPricing({
  campaigns,
  regularPrice,
  maxEligiblePriceForGift,
}: {
  campaigns: Record<string, CampaignInfo>
  regularPrice: number
  maxEligiblePriceForGift?: number
}): { campaignName: string; campaignData: CampaignInfo } | null {
  if (!campaigns) return null

  // Priority 1: Check buy_1_get_1_value_match campaign first (higher priority)
  const buy1Get1ValueMatchCampaign =
    campaigns[CAMPAIGN_NAMES.BUY_1_GET_1_VALUE_MATCH]

  if (
    buy1Get1ValueMatchCampaign?.isActive &&
    maxEligiblePriceForGift !== undefined &&
    maxEligiblePriceForGift > 0 &&
    regularPrice <= maxEligiblePriceForGift
  ) {
    return {
      campaignName: CAMPAIGN_NAMES.BUY_1_GET_1_VALUE_MATCH,
      campaignData: buy1Get1ValueMatchCampaign,
    }
  }

  // Priority 2: Fall back to buy_more_save_more campaign
  const buyMoreSaveMoreCampaign = campaigns[CAMPAIGN_NAMES.BUY_MORE_SAVE_MORE]

  if (buyMoreSaveMoreCampaign?.isActive) {
    return {
      campaignName: CAMPAIGN_NAMES.BUY_MORE_SAVE_MORE,
      campaignData: buyMoreSaveMoreCampaign,
    }
  }

  return null
}

/**
 * Check xem product có bị exclude khỏi campaign đang được áp dụng không
 * Tự động detect campaign đang được áp dụng và check exclude của campaign đó
 *
 * @param product - Product cần check
 * @param campaigns - Tất cả campaigns từ CampaignProvider
 * @param regularPrice - Giá thường của sản phẩm
 * @param maxEligiblePriceForGift - Giá tối đa được tặng từ cart-mini
 * @returns true nếu product bị exclude, false nếu không
 */
export function isProductExcludedByActiveCampaign({
  product,
  campaigns,
  regularPrice,
  maxEligiblePriceForGift,
}: {
  product: ProductFormatted | ProductDetailFormatted | undefined
  campaigns: Record<string, CampaignInfo>
  regularPrice: number
  maxEligiblePriceForGift?: number
}): boolean {
  const activeCampaign = getActiveCampaignForPricing({
    campaigns,
    regularPrice,
    maxEligiblePriceForGift,
  })

  if (!activeCampaign) {
    return false
  }

  return isProductExcluded(product, activeCampaign.campaignData)
}

/**
 * Extract và snapshot campaign data cần thiết từ objects
 * Giúp code ngắn gọn hơn nhưng vẫn explicit về fields được dùng
 *
 * @param cartMini - Cart mini data với max_promotion_amount và max_eligible_price_for_gift
 * @param campaignData - Campaign data với percent và exclude_collection_ids
 * @param isCampaignActive - Campaign active status cho buy_more_save_more
 * @param isBuy1Get1ValueMatchActive - Campaign active status cho buy_1_get_1_value_match
 * @returns Snapshot object với các fields đã extract
 */
export function extractCampaignSnapshot({
  cartMini,
  campaignData,
  isCampaignActive,
  isBuy1Get1ValueMatchActive,
}: {
  cartMini?: Pick<
    CartMiniResponse,
    'max_promotion_amount' | 'max_eligible_price_for_gift'
  >
  campaignData?: CampaignInfo
  isCampaignActive: boolean
  isBuy1Get1ValueMatchActive: boolean
}): {
  maxPromotionAmount: number
  campaignPercent: number
  isCampaignActive: boolean
  maxEligiblePriceForGift?: number
  isBuy1Get1ValueMatchActive: boolean
} {
  return {
    maxPromotionAmount: cartMini?.max_promotion_amount ?? 0,
    campaignPercent: (campaignData?.data?.percent ?? 0) as number,
    isCampaignActive,
    maxEligiblePriceForGift: cartMini?.max_eligible_price_for_gift,
    isBuy1Get1ValueMatchActive,
  }
}

/**
 * Tính display price và compare price từ campaign result
 * Logic giống với product listing để đảm bảo consistency
 *
 * @param campaignResult - Kết quả từ calculateCampaignPrice
 * @param regularPrice - Giá thường của sản phẩm
 * @param comparePrice - Giá so sánh (giá gốc) của sản phẩm
 * @returns Object với displayPrice và displayComparePrice
 */
export function getDisplayPriceFromCampaignResult({
  campaignResult,
  regularPrice,
  comparePrice,
}: {
  campaignResult: CampaignPriceResult
  regularPrice: number
  comparePrice: number
}): {
  displayPrice: number
  displayComparePrice: number
} {
  const displayPrice = campaignResult.shouldShowDiscount
    ? campaignResult.finalPrice
    : regularPrice

  const displayComparePrice =
    campaignResult.shouldShowDiscount &&
    campaignResult.originalPrice !== campaignResult.finalPrice
      ? campaignResult.originalPrice
      : comparePrice && comparePrice !== regularPrice
        ? comparePrice
        : 0

  return {
    displayPrice,
    displayComparePrice,
  }
}
